import { useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useTranslate,
} from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import type { NevoraDataProvider } from "../datos/dataProvider";
import type { Aviso } from "../dominio/tipos";
import { formatoFechaHora } from "../negocios/formato";
import { useAbrirFicha } from "../negocios/useAbrirFicha";
import { useNavigate } from "react-router";

/**
 * Campana de avisos (S10): leads asignados a mano, resumen diario del reparto, leads
 * retirados por falta de contacto y, para Jan y Robert, cada consultoría que agenda
 * alguien. Cada uno ve solo los suyos (RLS). Pulsar un aviso lo marca leído y abre la ficha.
 */
export const CampanaAvisos = ({
  plegada = false,
  className,
  alinear = "start",
}: {
  plegada?: boolean;
  className?: string;
  /** Lado del menú respecto a la campana (en la esquina superior derecha, «end»). */
  alinear?: "start" | "end";
}) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { identity } = useGetIdentity();
  const abrirFicha = useAbrirFicha();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const [abierta, setAbierta] = useState(false);
  // Auditoría 30/10/2026: un aviso abierto no desaparece para siempre: «Ver anteriores» (30 días).
  const [anteriores, setAnteriores] = useState(false);
  const { data: leidos } = useGetList<Aviso>(
    "avisos",
    {
      pagination: { page: 1, perPage: 30 },
      sort: { field: "created_at", order: "DESC" },
      filter: {
        "leido_at@not.is": null,
        "created_at@gte": new Date(Date.now() - 30 * 86_400_000).toISOString(),
      },
    },
    { enabled: !!identity?.id && abierta && anteriores },
  );
  const { data: avisos } = useGetList<Aviso>(
    "avisos",
    {
      pagination: { page: 1, perPage: 30 },
      sort: { field: "created_at", order: "DESC" },
      filter: { "leido_at@is": null },
    },
    { enabled: !!identity?.id, refetchInterval: 60_000 },
  );
  const n = avisos?.length ?? 0;

  const marcar = async (ids: Aviso["id"][]) => {
    try {
      await dataProvider.marcarAvisosLeidos(ids);
    } finally {
      await queryClient.invalidateQueries({ queryKey: ["avisos"] });
    }
  };

  const etiqueta = translate("nevora.avisos.titulo");
  return (
    <Popover open={abierta} onOpenChange={setAbierta}>
      <PopoverTrigger asChild>
        <Button
          data-guia="avisos"
          variant="ghost"
          className={cn(
            "relative",
            plegada ? "size-9 p-0" : "h-9 justify-start gap-3 px-2.5",
            className,
          )}
          aria-label={
            n
              ? `${etiqueta}: ${translate("nevora.avisos.sin_leer", { smart_count: n })}`
              : etiqueta
          }
          title={plegada ? etiqueta : undefined}
          data-testid="campana-avisos"
        >
          <Bell className="size-[18px] shrink-0" />
          {!plegada ? (
            <span className="text-sm font-medium text-muted-foreground">
              {etiqueta}
            </span>
          ) : null}
          {n ? (
            <span
              className={cn(
                "inline-flex min-w-5 h-5 items-center justify-center rounded-full bg-[#3D8BD4] px-1 text-[11px] font-semibold text-white",
                plegada ? "absolute -top-1 -right-1" : "ml-auto",
              )}
            >
              {n > 9 ? "9+" : n}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align={alinear} className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-semibold">{etiqueta}</span>
          {n ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => marcar((avisos ?? []).map((a) => a.id))}
            >
              {translate("nevora.avisos.marcar_todos")}
            </Button>
          ) : null}
        </div>
        {n ? (
          <ul className="max-h-96 overflow-y-auto">
            {avisos!.map((a) => (
              <li key={String(a.id)} className="border-b last:border-b-0">
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left hover:bg-muted"
                  onClick={() => {
                    void marcar([a.id]);
                    setAbierta(false);
                    if (a.negocio_id != null) abrirFicha(a.negocio_id);
                    // 43: una colaboración de la web no tiene ficha: a su lista.
                    else if (a.tipo === "colaboracion_web")
                      navigate("/colaboraciones");
                  }}
                >
                  {a.titulo ? (
                    <span className="block text-sm font-medium">
                      {a.titulo}
                    </span>
                  ) : null}
                  <span className="block text-sm">{a.texto}</span>
                  <span className="block text-xs text-muted-foreground">
                    {formatoFechaHora(a.created_at)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            {translate("nevora.avisos.vacio")}
          </p>
        )}
        <div className="border-t">
          <button
            type="button"
            className="w-full px-3 py-2 text-left text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={() => setAnteriores((v) => !v)}
            aria-expanded={anteriores}
          >
            {translate(
              anteriores
                ? "nevora.avisos.ocultar_anteriores"
                : "nevora.avisos.ver_anteriores",
            )}
          </button>
          {anteriores ? (
            <ul
              className="max-h-64 overflow-y-auto"
              data-testid="avisos-anteriores"
            >
              {(leidos ?? []).length ? (
                (leidos ?? []).map((a) => (
                  <li key={String(a.id)} className="border-t first:border-t-0">
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-left opacity-70 hover:bg-muted hover:opacity-100"
                      onClick={() => {
                        setAbierta(false);
                        if (a.negocio_id != null) abrirFicha(a.negocio_id);
                        else if (a.tipo === "colaboracion_web")
                          navigate("/colaboraciones");
                      }}
                    >
                      {a.titulo ? (
                        <span className="block text-sm font-medium">
                          {a.titulo}
                        </span>
                      ) : null}
                      <span className="block text-sm">{a.texto}</span>
                      <span className="block text-xs text-muted-foreground">
                        {formatoFechaHora(a.created_at)}
                      </span>
                    </button>
                  </li>
                ))
              ) : (
                <li className="px-3 pb-3 text-xs text-muted-foreground">
                  {translate("nevora.avisos.sin_anteriores")}
                </li>
              )}
            </ul>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
};
